import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { computed, type MaybeRefOrGetter, toValue } from "vue";
import { request } from "@/shared/http/client";

/** The internal bell — where a function's `ctx.notify` finally lands. */
export interface Notification {
	readonly id: string;
	/**
	 * Whose row this is, or `null` for a notice addressed to the whole tenant.
	 *
	 * Read by the panel for one decision only: a tenant-wide notice is a SINGLE row
	 * shared by every colleague, so it cannot be dismissed by one of them. See
	 * `dismissible`.
	 */
	readonly userId: string | null;
	readonly title: string;
	readonly body: string | null;
	readonly severity: "info" | "warning" | "urgent";
	readonly subjectKind: string | null;
	readonly subjectId: string | null;
	/**
	 * Which entity or funnel the subject lives in.
	 *
	 * The half that was missing: a `record` notice arrived with a uuid and nothing
	 * that could build an address out of it, so the bell showed a line of text where
	 * a link belonged. See `linkFor`.
	 */
	readonly subjectCode: string | null;
	/** Whether the person was ALSO reached outside the panel. */
	readonly medium: "bell" | "whatsapp" | "email";
	/** What the rule asked for — `team:<uuid>` — not who it turned out to be. */
	readonly target: string | null;
	readonly origin: string;
	readonly readAt: string | null;
	readonly createdAt: string;
}

/**
 * Where clicking a notice goes, or `null` when there is nowhere honest to send it.
 *
 * Null rather than the home page: a bell that always navigates teaches people that
 * clicking it does nothing useful, and they stop.
 */
export function linkFor(item: Notification): string | null {
	if (!item.subjectId) return null;
	if (item.subjectKind === "record") {
		// The record route needs both halves; without the entity code the id names
		// nothing, because records live in the tenant's own schema.
		return item.subjectCode ? `/e/${item.subjectCode}/${item.subjectId}` : null;
	}
	if (item.subjectKind === "human_request") {
		// A knowledge proposal (dictated to Ben) is approved in Memory, not in Help requests.
		return item.subjectCode === "dictation" ? "/memory?aba=curated" : "/help";
	}
	if (item.subjectKind === "mission_run") return "/missions";
	if (item.subjectKind === "conversation") return `/inbox/${item.subjectId}`;
	// `card` deliberately has no link yet: the board addresses a pipeline, not one
	// card, so it would land somebody on a screen and leave them looking for it.
	return null;
}

/**
 * Whether this line can be taken off the list.
 *
 * A notice with no owner is ONE row that the whole tenant reads, so dismissing it
 * would clear it from every colleague's bell. The server refuses it either way;
 * this is what keeps the panel from drawing an X that would do nothing.
 */
export function dismissible(item: Notification): boolean {
	return item.userId !== null;
}

/**
 * The two halves of the open bell: what is still to read, and what came before.
 *
 * The ORDER is the server's (`read_at IS NULL DESC`, 29/09) — it has to be, since
 * the list is paged. What this adds is `held`: the lines that were unread while
 * the panel was open stay in the top half after being clicked, so the row under
 * the cursor does not jump down past the separator the instant the refetch lands.
 * They move on the next opening.
 */
export function splitUnread(
	items: readonly Notification[],
	held: ReadonlySet<string>,
): { unread: Notification[]; earlier: Notification[] } {
	const top = (n: Notification) => !n.readAt || held.has(n.id);
	return {
		// Re-sorted by date because a held line comes back from the server among the
		// read ones; `sort` is stable, so equal timestamps keep the server's order.
		unread: items.filter(top).sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)),
		earlier: items.filter((n) => !top(n)),
	};
}

export const notificationKeys = {
	all: () => ["notifications"] as const,
	list: (unreadOnly: boolean) => ["notifications", "list", unreadOnly] as const,
};

export const NOTIFICATION_PAGE = 50;

export interface NotificationPage {
	readonly items: Notification[];
	/** How many the SAME filter has in all — what says whether there is more. */
	readonly total: number;
	/** The badge, which ignores the filter: it answers "chegou algo", not "o que estou vendo". */
	readonly unread: number;
}

/**
 * The bell, a page at a time and filtered.
 *
 * The filter is part of the KEY. With `["notifications"]` fixed, turning "só não
 * lidas" on would have been answered out of cache with the list that had them all,
 * and the panel would light the filter up while showing the same rows.
 *
 * `useInfiniteQuery` and not an array of extra pages kept by hand: everything here
 * changes under the user's feet — an event on the notification stream, a line
 * marked read, a line dismissed — and invalidating refetches ALL the loaded pages,
 * so the pages past the first stay as true as the first one.
 */
export function useNotifications(unreadOnly: MaybeRefOrGetter<boolean>) {
	const query = useInfiniteQuery({
		queryKey: computed(() => notificationKeys.list(toValue(unreadOnly))),
		initialPageParam: 0,
		queryFn: async ({ pageParam }): Promise<NotificationPage> => {
			const p = new URLSearchParams({
				limit: String(NOTIFICATION_PAGE),
				offset: String(pageParam),
			});
			// Sent only when true: `z.coerce.boolean()` on the server reads the string,
			// and the string "false" coerces to true.
			if (toValue(unreadOnly)) p.set("unreadOnly", "true");
			return request(`/v3/notifications?${p.toString()}`);
		},
		getNextPageParam: (last, pages) => {
			const loaded = pages.reduce((n, page) => n + page.items.length, 0);
			return loaded < last.total ? loaded : undefined;
		},
		// SEM relógio (27/08, zero polling): o sino atualiza no foco da janela, nas
		// mutações e — desde 05/09 — no fio `GET /v3/notifications/stream`, que
		// `useNotificationStream` ouve enquanto o sino está montado e que invalida
		// estas chaves a cada escrita no servidor. O foco fica como rede por baixo.
		refetchInterval: false,
		refetchOnWindowFocus: true,
		// Flipping the filter keeps the old rows on screen until the new ones land.
		// Without it the panel empties for an instant and the badge blinks to zero.
		placeholderData: (previous) => previous,
	});

	return {
		items: computed<Notification[]>(() => query.data.value?.pages.flatMap((p) => p.items) ?? []),
		total: computed(() => query.data.value?.pages[0]?.total ?? 0),
		unread: computed(() => query.data.value?.pages[0]?.unread ?? 0),
		hasMore: query.hasNextPage,
		loadingMore: query.isFetchingNextPage,
		loadMore: () => void query.fetchNextPage(),
	};
}

export function useMarkRead() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (input: { ids?: string[]; all?: boolean }) =>
			request<{ marked: number }>("/v3/notifications/read", { method: "POST", body: input }),
		onSuccess: () => client.invalidateQueries({ queryKey: notificationKeys.all() }),
	});
}

/**
 * Off the list for good.
 *
 * Separate from `markRead` because it is a different sentence: read means "eu vi",
 * dismissed means "não quero mais ver". Confusing the two is how a bell ends up
 * with "marcar todas como lidas" as its only gesture.
 */
export function useDismiss() {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (ids: string[]) =>
			request<{ dismissed: number }>("/v3/notifications/dismiss", {
				method: "POST",
				body: { ids },
			}),
		onSuccess: () => client.invalidateQueries({ queryKey: notificationKeys.all() }),
	});
}
